import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ANALYTICS_ID = 'G-VR6VM1B6J7';
export const CONSENT_STORAGE_KEY = 'ambitus-analytics-consent';
const GOOGLE_TAG_SCRIPT_ID = 'ambitus-google-analytics';

function readSavedConsent() {
  try {
    const savedConsent = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return ['accepted', 'rejected'].includes(savedConsent) ? savedConsent : null;
  } catch {
    return null;
  }
}

function saveConsent(value) {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch {
    // Consent still applies to this page view if browser storage is unavailable.
  }
}

function updateGoogleConsent(analyticsStorage) {
  window.gtag?.('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: analyticsStorage,
  });
}

export function enableAnalytics() {
  updateGoogleConsent('granted');

  if (!document.getElementById(GOOGLE_TAG_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = GOOGLE_TAG_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${ANALYTICS_ID}`;
    document.head.appendChild(script);
  }

  if (!window.__ambitusAnalyticsConfigured) {
    window.gtag?.('js', new Date());
    window.gtag?.('config', ANALYTICS_ID);
    window.__ambitusAnalyticsConfigured = true;
  }
}

function removeAnalyticsCookies() {
  const cookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0].trim())
    .filter((name) => name === '_ga' || name.startsWith('_ga_'));

  cookieNames.forEach((name) => {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
  });
}

export function disableAnalytics() {
  updateGoogleConsent('denied');
  removeAnalyticsCookies();
}

export default function CookieConsent() {
  const { t } = useTranslation();
  const [consent, setConsent] = useState(readSavedConsent);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (consent === 'accepted') {
      enableAnalytics();
    } else if (consent === 'rejected') {
      disableAnalytics();
    }
  }, [consent]);

  useEffect(() => {
    const openSettings = () => setSettingsOpen(true);
    window.addEventListener('ambitus:open-cookie-settings', openSettings);
    return () => window.removeEventListener('ambitus:open-cookie-settings', openSettings);
  }, []);

  const chooseConsent = (choice) => {
    saveConsent(choice);
    setConsent(choice);
    setSettingsOpen(false);
  };

  if (consent && !settingsOpen) {
    return null;
  }

  return (
    <section
      className="cookie-consent"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
    >
      <div className="cookie-consent__content">
        <div>
          <p className="cookie-consent__eyebrow">{t('cookies.eyebrow')}</p>
          <h2 id="cookie-consent-title">{t('cookies.title')}</h2>
          <p id="cookie-consent-description">
            {t('cookies.description')}{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer"
            >
              {t('cookies.googlePrivacy')}
            </a>
          </p>
        </div>

        <div className="cookie-consent__actions">
          <button type="button" onClick={() => chooseConsent('rejected')}>
            {t('cookies.reject')}
          </button>
          <button
            className="cookie-consent__accept"
            type="button"
            onClick={() => chooseConsent('accepted')}
          >
            {t('cookies.accept')}
          </button>
          {consent && (
            <button
              className="cookie-consent__keep"
              type="button"
              onClick={() => setSettingsOpen(false)}
            >
              {t('cookies.keep')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
